import { CheckCircleRounded, FileDownloadOutlined, KeyboardArrowRight, StopCircleOutlined } from '@mui/icons-material'
import { Box, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import EllipsisTypography from '@/components/dataDisplay/EllipsisTypography'
import CustomButton from '@/components/inputs/CustomButton'
import PhasePips from '@/features/prequalification/localControl/components/PhasePips'
import type { ResourceSetTestActions } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import { testKey } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import RunTestButton from '@/features/prequalification/localControl/components/RunTestButton'
import TestResourceRow from '@/features/prequalification/localControl/components/TestResourceRow'
import type { LocalPqRun, PqTestName, ResourceSetMember } from '@/features/prequalification/localControl/types'
import { aggregateTest } from '@/features/prequalification/localControl/utils/aggregation'
import { settleInSx } from '@/features/prequalification/localControl/utils/motion'
import { isTerminalPhase } from '@/features/prequalification/types'

type TestRowProps = {
  testName: PqTestName
  label: string
  resources: ResourceSetMember[]
  /** Runs for the owning market; this row filters them down to its test. */
  runs: LocalPqRun[]
  actions: ResourceSetTestActions
}

function TestRow({ testName, label, resources, runs, actions }: Readonly<TestRowProps>) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const aggregate = aggregateTest(runs, testName, resources.length)
  const runByResource = new Map(aggregate.runs.map((run) => [run.resourceId, run]))
  const isActive = aggregate.runs.some((run) => !isTerminalPhase(run.phase))
  const isPending = actions.pendingKeys.has(testKey(testName))
  const canExpand = resources.length > 0
  const toggleExpanded = () => setIsExpanded((open) => !open)

  /**
   * Why the combined CSV cannot be downloaded, if it cannot. The backend merges every
   * resource's run of the test into one file and refuses while any of them is still
   * going, so saying that here is better than letting the click fail and explaining
   * afterwards. A run of its own is downloadable while in progress — partial chunks are
   * still results — which is why only this row's button carries the restriction.
   */
  function combinedCsvBlockedReason(): string | undefined {
    if (!aggregate.started) {
      return t('prequalifications.resource_set.test.download_blocked_not_started')
    }
    if (isActive) {
      return t('prequalifications.resource_set.test.download_blocked_active')
    }
    return undefined
  }

  const downloadBlockedReason = combinedCsvBlockedReason()
  // Pending is blocked too, but it is a moment long and self-evident from the spinner
  // it accompanies, so it gets no sentence of its own.
  const isDownloadBlocked = isPending || downloadBlockedReason !== undefined

  // Settle the success mark in only when the test crosses into fully passed
  // while the user is watching, not on every render it stays green.
  const wasPassed = useRef(aggregate.isPassed)
  const justPassed = !wasPassed.current && aggregate.isPassed
  useEffect(() => {
    wasPassed.current = aggregate.isPassed
  })

  const pips = resources.map((resource) => {
    const run = runByResource.get(resource.resourceId)
    return { key: resource.resourceId, phase: run?.phase ?? null, startAt: run?.startAt ?? null }
  })

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          py: 1,
          px: 1,
          cursor: canExpand ? 'pointer' : 'default',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onClick={canExpand ? toggleExpanded : undefined}
      >
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <IconButton
            aria-expanded={isExpanded}
            aria-label={t('prequalifications.resource_set.test.toggle_resources', { test: label })}
            disabled={!canExpand}
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              toggleExpanded()
            }}
          >
            <KeyboardArrowRight
              sx={{ transition: 'transform 150ms ease', transform: isExpanded ? 'rotate(90deg)' : 'none' }}
            />
          </IconButton>
          <EllipsisTypography sx={{ minWidth: 0 }} tooltipTitle={label} variant="body1">
            {label}
          </EllipsisTypography>
        </Stack>

        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {aggregate.started && <PhasePips pips={pips} />}
          {aggregate.isPassed && (
            <CheckCircleRounded color="success" sx={[{ fontSize: 16 }, justPassed && settleInSx]} />
          )}
          <Typography
            color={aggregate.isPassed ? 'success.main' : 'text.secondary'}
            sx={{ whiteSpace: 'nowrap', fontWeight: aggregate.isPassed ? 600 : 400 }}
            variant="body2"
          >
            {aggregate.started
              ? t('prequalifications.resource_set.test.passed_count', {
                  passed: aggregate.passed,
                  total: aggregate.total,
                })
              : t('prequalifications.resource_set.test.not_started')}
          </Typography>
        </Stack>

        <Stack
          sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}
          onClick={(event) => event.stopPropagation()}
        >
          <RunTestButton
            disabled={isPending || resources.length === 0}
            label={
              aggregate.started
                ? t('prequalifications.resource_set.test.run_again')
                : t('prequalifications.resource_set.test.start')
            }
            testId="run-test-button"
            onRun={(startAt) => actions.onRunTest(testName, startAt)}
          />
          {isActive && (
            <CustomButton
              color="error"
              disabled={isPending}
              size="small"
              startIcon={<StopCircleOutlined />}
              variant="outlined"
              onClick={() => actions.onStopTest(testName)}
            >
              {t('prequalifications.resource_set.test.stop')}
            </CustomButton>
          )}
          <Tooltip
            // With a reason to give, it becomes the button's description rather than its
            // label: `describeChild` leaves the aria-label as the name and hangs the reason
            // off aria-describedby, so focus reaches it too. Without one the tooltip is just
            // the label again, and naming the button twice would be worse than saying nothing.
            describeChild={downloadBlockedReason !== undefined}
            title={downloadBlockedReason ?? t('prequalifications.resource_set.test.download_csv')}
          >
            <IconButton
              // aria-disabled rather than disabled: a disabled IconButton takes no pointer
              // events and leaves the focus order, so neither the mouse nor the keyboard could
              // reach the reason. The click below is what refuses. This also drops the wrapper
              // span a disabled child would need for the tooltip to work at all.
              aria-disabled={isDownloadBlocked || undefined}
              aria-label={t('prequalifications.resource_set.test.download_csv')}
              data-testid="test-download-button"
              sx={
                isDownloadBlocked
                  ? {
                      cursor: 'default',
                      opacity: (theme) => theme.palette.action.disabledOpacity,
                      '&:hover': { backgroundColor: 'transparent' },
                    }
                  : undefined
              }
              onClick={() => {
                if (isDownloadBlocked) {
                  return
                }
                actions.onDownloadTest(testName)
              }}
            >
              <FileDownloadOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse unmountOnExit in={isExpanded}>
        <Box sx={{ backgroundColor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          {resources.map((resource) => (
            <TestResourceRow
              key={resource.resourceId}
              actions={actions}
              resource={resource}
              run={runByResource.get(resource.resourceId) ?? null}
              testName={testName}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

export default TestRow
